import { Component, signal, OnInit, OnDestroy, inject, ElementRef } from '@angular/core';
import { FAQ_SECTIONS } from '../app.constants';
import { NeedHelpService } from '../services/need-help.service';

@Component({
  selector: 'app-faq',
  imports: [],
  templateUrl: './faq-page.html',
  styleUrl: './faq-page.scss',
})
export class FaqPage implements OnInit, OnDestroy {
  openItem = signal<string | null>(null);
  activeSection = signal<string>('getting-started');
  needHelp = inject(NeedHelpService);
  readonly sections = FAQ_SECTIONS;

  private scrollListener?: () => void;
  private el = inject(ElementRef);
  private isProgrammaticScroll = false;
  private programmaticScrollTimer?: ReturnType<typeof setTimeout>;

  paragraphs(text: string): string[] {
    return text.split('\n\n').filter((p) => p.trim());
  }

  // Reads the first sidebar link's current rendered position — works at any zoom level.
  // Used by the scroll spy (dynamic: matches sidebar's actual position while scrolling).
  private triggerOffset(scrollEl: HTMLElement): number {
    const link = scrollEl.querySelector<HTMLElement>('.faq__sidebar-link');
    return link ? link.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top : 48;
  }

  // Reads the sidebar's CSS sticky `top` value — used by scrollTo() as the scroll target.
  // The sidebar may not be sticky yet when a tab is clicked, so we use the declared
  // CSS value (the position it will settle at after scrolling), not the live position.
  private stickyTop(scrollEl: HTMLElement): number {
    const sidebar = scrollEl.querySelector<HTMLElement>('.faq__sidebar');
    return sidebar ? parseFloat(getComputedStyle(sidebar).top) : 48;
  }

  ngOnInit(): void {
    const scrollEl = this.el.nativeElement as HTMLElement;
    const lastSectionId = this.sections[this.sections.length - 1].id;

    const onScroll = () => {
      if (this.isProgrammaticScroll) return;

      const isAtBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 2;

      if (isAtBottom) {
        this.activeSection.set(lastSectionId);
        return;
      }

      const containerTop = scrollEl.getBoundingClientRect().top;
      const offset = this.triggerOffset(scrollEl);
      let active = this.sections[0].id;

      for (const section of this.sections) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - containerTop <= offset + 1) {
          active = section.id;
        }
      }
      this.activeSection.set(active);
    };

    this.scrollListener = onScroll;
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(onScroll);
  }

  ngOnDestroy(): void {
    const scrollEl = this.el.nativeElement as HTMLElement;
    if (this.scrollListener) {
      scrollEl.removeEventListener('scroll', this.scrollListener);
    }
    clearTimeout(this.programmaticScrollTimer);
  }

  toggle(id: string): void {
    this.openItem.set(this.openItem() === id ? null : id);
  }

  scrollTo(id: string): void {
    this.activeSection.set(id);
    const scrollEl = this.el.nativeElement as HTMLElement;
    const el = document.getElementById(id);
    if (!el) return;

    // Lock the spy so the smooth-scroll animation cannot override the active state
    this.isProgrammaticScroll = true;
    clearTimeout(this.programmaticScrollTimer);
    const unlock = () => {
      this.isProgrammaticScroll = false;
    };
    scrollEl.addEventListener('scrollend', unlock, { once: true });
    this.programmaticScrollTimer = setTimeout(unlock, 1000);

    const isLastSection = id === this.sections[this.sections.length - 1].id;
    if (isLastSection) {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' });
      return;
    }

    const containerTop = scrollEl.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top - containerTop;
    const targetScrollTop = scrollEl.scrollTop + elTop - this.stickyTop(scrollEl);
    scrollEl.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  }
}
